/**
 * SessionPrepScreen — AI-powered session preparation
 * Route: /session/:id/prep
 * Sprint 1 — new screen
 */

import { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import {
  ArrowLeft, Zap, Send, Bookmark, FileText,
  Clock, MapPin, ChevronRight, Loader2, MessageCircle, BookOpen,
  RefreshCw, Download,
} from 'lucide-react';
import { notify } from '../utils/notifications';
import { useApp } from '../context/AppContext';
import { useEventData } from '../context/EventDataContext';
import {
  generateAIResponse,
  getQuickInsight,
  filterActionChips,
  type AIContext,
  type ActionChip,
} from '../services/aiService';
import chatThreadService from '../services/chatThreadService';
import { useEventModules } from '../hooks/useEventModules';
import { MarkdownContent } from '../components/MarkdownContent';
import { ReportAIResponseButton } from '../components/ReportAIResponseButton';
import { isSessionPrepEnabled, type Session } from '@conference/shared';
import { previewCopilotResponse, previewSessionBrief } from '../preview/previewAI';

// ─── Time formatting helpers ──────────────────────────────────────────────────

function formatTimeRange(start: string, end: string): string {
  const fmt = (t: string) => {
    const [h, m] = t.split(':').map(Number);
    const ampm = h < 12 ? 'AM' : 'PM';
    const h12 = h % 12 || 12;
    return `${h12}:${m.toString().padStart(2, '0')} ${ampm}`;
  };
  return `${fmt(start)} – ${fmt(end)}`;
}

function formatDuration(start: string, end: string): string {
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  const diff = (eh * 60 + em) - (sh * 60 + sm);
  if (diff <= 0) return '';
  if (diff < 60) return `${diff} min`;
  const mins = diff % 60;
  return mins ? `${Math.floor(diff / 60)}h ${mins}min` : `${Math.floor(diff / 60)}h`;
}

// ─── Icon resolver ────────────────────────────────────────────────────────────

const CHIP_ICONS: Record<string, React.ReactNode> = {
  MessageCircle: <MessageCircle className="w-3.5 h-3.5" />,
  FileText:      <FileText className="w-3.5 h-3.5" />,
  BookOpen:      <BookOpen className="w-3.5 h-3.5" />,
  Bookmark:      <Bookmark className="w-3.5 h-3.5" />,
  RefreshCw:     <RefreshCw className="w-3.5 h-3.5" />,
  Download:      <Download className="w-3.5 h-3.5" />,
  Zap:           <Zap className="w-3.5 h-3.5" />,
};

function ChipIcon({ name }: { name: string }) {
  return <>{CHIP_ICONS[name] ?? <Zap className="w-3.5 h-3.5" />}</>;
}

// ─── Types ────────────────────────────────────────────────────────────────────

type Message = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  chips?: ActionChip[];
  receiptId?: string;
  timestamp: Date;
};

type PrepState = 'loading' | 'ready' | 'error';

// ─── Main Component ───────────────────────────────────────────────────────────

export default function SessionPrepScreen() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { addNote, aiPreferences, authSession, currentEventId, isAuthSessionCurrent, selectedEvent } = useApp();
  const { isEnabled, modules } = useEventModules();
  const { preview, services: { sessionService } } = useEventData();
  const moduleSignature = JSON.stringify(selectedEvent?.app_modules ?? {});

  const [session, setSession] = useState<Session | null>(null);
  const [sessionLoadFailed, setSessionLoadFailed] = useState(false);
  const [prepState, setPrepState] = useState<PrepState>('loading');
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [progressLabel, setProgressLabel] = useState('');
  const [isSaved, setIsSaved] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const threadIdRef = useRef<string | null>(null);
  const threadSummaryRef = useRef<string | null>(null);

  // ── Load real session from Supabase ──────────────────────────────────────
  useEffect(() => {
    if (!id || !currentEventId) { setSessionLoadFailed(true); return; }
    const owner = authSession;
    if (!preview && (!owner || !isAuthSessionCurrent(owner))) return;
    const isCurrent = () => preview || (!!owner && isAuthSessionCurrent(owner));
    let disposed = false;
    void (async () => {
      try {
        const loaded = await sessionService.getById(
          id,
          currentEventId,
          preview ? undefined : { signal: owner!.signal },
        );
        if (disposed || !isCurrent()) return;
        if (loaded) setSession(loaded);
        else setSessionLoadFailed(true);
      } catch {
        if (!disposed && isCurrent()) {
          setSessionLoadFailed(true);
        }
      }
    })();
    return () => { disposed = true; };
  }, [authSession, currentEventId, id, isAuthSessionCurrent, preview, sessionService]);

  const aiContext: AIContext | null = session ? {
    mode: 'session-prep',
    sessionId: session.id,
    sessionTitle: session.title,
    sessionTime: formatTimeRange(session.start_time, session.end_time),
    sessionTrack: session.track,
    eventId: session.event_id,
  } : null;
  const prepEnabled = !session || isSessionPrepEnabled(selectedEvent?.app_modules, session.type);

  // ── Load prep brief — from cache if available, otherwise LLM ─────────────
  useEffect(() => {
    if (!session || !aiContext || !prepEnabled) return;
    if (preview) {
      setMessages([{
        id: 'preview-prep-brief',
        role: 'assistant',
        content: previewSessionBrief(session),
        timestamp: new Date(),
      }]);
      setPrepState('ready');
      return;
    }
    const owner = authSession;
    if (!owner || !isAuthSessionCurrent(owner)) return;
    let disposed = false;
    const stillCurrent = () =>
      !disposed && isAuthSessionCurrent(owner);
    setMessages([]);
    setPrepState('loading');

    const load = async () => {
      try {
        threadIdRef.current = null;
        threadSummaryRef.current = null;
        if (!stillCurrent()) return;
        // Find or create the persistent thread for this session
        const thread = await chatThreadService.findOrCreate(
          {
            event_id: session.event_id ? String(session.event_id) : undefined,
            context_type: 'session',
            context_id: session.id,
            title: 'Session Prep',
          },
          { signal: owner.signal },
        );
        if (!stillCurrent()) return;
        threadIdRef.current = thread.id;
        threadSummaryRef.current = thread.summary ?? null;

        // Load existing messages from the thread
        if (!stillCurrent()) return;
        const existing = await chatThreadService.getMessages(
          thread.id,
          { signal: owner.signal },
        );
        if (!stillCurrent()) return;
        if (existing.length > 0) {
          setMessages(existing.map(m => ({
            id: m.id,
            role: m.role,
            content: m.content,
            receiptId: m.receipt_id ?? undefined,
            timestamp: new Date(m.created_at),
          })));
          setPrepState('ready');
          return;
        }

        // No cached messages — check auto-insights preference
        if (!aiPreferences.aiAutoInsights) {
          if (!stillCurrent()) return;
          setMessages([{
            id: 'prep-manual',
            role: 'assistant',
            content: [
              '## Session Prep Ready',
              '',
              'Auto-insights are turned off in your AI preferences. You can still ask me anything about this session using the input below, or tap "Generate Prep Brief" to get a full briefing.',
              '',
              '- Type a question about the session topic',
              ...(isEnabled('speakers') ? ['- Ask for speaker background'] : []),
              '- Request key concepts to review',
            ].join('\n'),
            chips: [{ label: 'Generate Prep Brief', action: 'regenerate', icon: 'Zap' }],
            timestamp: new Date(),
          }]);
          setPrepState('ready');
          return;
        }

        // First visit — call LLM and persist the result
        if (!stillCurrent()) return;
        const response = await getQuickInsight(
          aiContext,
          undefined,
          owner.signal,
        );
        if (!stillCurrent()) return;
        const brief: Message = {
          id: 'prep-brief',
          role: 'assistant',
          content: response.content,
          chips: response.chips,
          receiptId: response.receiptId,
          timestamp: new Date(),
        };
        setMessages([brief]);
        setPrepState('ready');
        if (!stillCurrent()) return;
        const appendResult = await chatThreadService.appendMessages(
          thread.id,
          [{ role: 'assistant', content: response.content, receipt_id: response.receiptId }],
          { signal: owner.signal },
        );
        if (!stillCurrent()) return;
        threadSummaryRef.current = appendResult.summary ?? null;
      } catch {
        if (stillCurrent()) setPrepState('error');
      }
    };
    void load();
    return () => { disposed = true; };
  }, [
    aiPreferences.aiAutoInsights,
    authSession,
    isAuthSessionCurrent,
    moduleSignature,
    session?.id,
    prepEnabled,
    preview,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView?.({ behavior: 'smooth' });
  }, [messages, isLoading]);

  const sendMessage = async (text?: string) => {
    const content = text || input.trim();
    const owner = authSession;
    if (
      !content ||
      isLoading ||
      !aiContext ||
      (!preview && (!owner || !isAuthSessionCurrent(owner)))
    ) return;

    setInput('');
    const userMsg: Message = {
      id: Date.now().toString(),
      role: 'user',
      content,
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, userMsg]);

    if (preview) {
      setInput('');
      setMessages(prev => [...prev, {
        id: `${Date.now()}-preview`,
        role: 'assistant',
        content: previewCopilotResponse('session'),
        timestamp: new Date(),
      }]);
      return;
    }

    if (!owner) return;
    setIsLoading(true);
    setProgressLabel('Thinking…');

    try {
      const history = messages.slice(-20).map(m => ({ role: m.role, content: m.content }));
      const response = await generateAIResponse(content, { ...aiContext, threadId: threadIdRef.current ?? undefined }, status => {
        if (isAuthSessionCurrent(owner)) setProgressLabel(status);
      }, undefined, history, threadSummaryRef.current ?? undefined, owner.signal);
      if (!isAuthSessionCurrent(owner)) return;
      const aiMsg: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: response.content,
        chips: response.chips,
        receiptId: response.receiptId,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, aiMsg]);
      if (threadIdRef.current) {
        if (!isAuthSessionCurrent(owner)) return;
        const threadId = threadIdRef.current;
        const result = await chatThreadService.appendMessages(
          threadId,
          [
            { role: 'user', content },
            { role: 'assistant', content: response.content, receipt_id: response.receiptId },
          ],
          { signal: owner.signal },
        );
        if (!isAuthSessionCurrent(owner)) return;
        threadSummaryRef.current = result.summary ?? null;
      }
    } catch {
      if (isAuthSessionCurrent(owner)) {
        notify.error('Could not generate a response', 'Please try again.');
      }
    } finally {
      if (isAuthSessionCurrent(owner)) {
        setIsLoading(false);
        setProgressLabel('');
      }
    }
  };

  const handleChipAction = (chip: ActionChip) => {
    switch (chip.action) {
      case 'more-questions':
        if (!isEnabled('speakers')) return;
        sendMessage('Generate 5 more sharp questions I could ask the speaker during Q&A');
        break;
      case 'take-notes':
        if (!isEnabled('notes')) return;
        navigate(`/notes`);
        break;
      case 'related':
        if (!isEnabled('papers')) return;
        sendMessage('What are the most relevant papers and sessions I should explore before this talk?');
        break;
      case 'save': {
        if (!isEnabled('notes')) return;
        const owner = authSession;
        if (!session || !owner || !isAuthSessionCurrent(owner)) return;
        setIsSaved(true);
        const lastAI = [...messages].reverse().find(m => m.role === 'assistant');
        if (lastAI) {
          const saved = addNote({
            title: `Session Prep — ${session.title}`,
            content: lastAI.content,
            source: 'ai-session-prep',
            sourceLabel: 'AI Session Prep',
            sessionId: session.id,
            sessionTitle: session.title,
          }, owner);
          if (!saved) setIsSaved(false);
        }
        if (isAuthSessionCurrent(owner)) {
          notify.success('Prep brief saved', 'Find it in My Notes');
        }
        break;
      }
      case 'follow-up':
        inputRef.current?.focus();
        break;
      case 'regenerate':
        handleRegenerate();
        break;
      default:
        notify.info(chip.label);
    }
  };

  const saveLatestAsNote = () => {
    if (!isEnabled('notes')) return;
    handleChipAction({ label: 'Save', action: 'save', icon: 'Bookmark' });
  };

  const handleRegenerate = async () => {
    if (preview && session) {
      setMessages([{
        id: `preview-prep-${Date.now()}`,
        role: 'assistant',
        content: previewSessionBrief(session),
        timestamp: new Date(),
      }]);
      setPrepState('ready');
      return;
    }
    const owner = authSession;
    if (!aiContext || !owner || !isAuthSessionCurrent(owner)) return;
    setPrepState('loading');
    setMessages([]);
    try {
      const response = await getQuickInsight(
        aiContext,
        undefined,
        owner.signal,
      );
      if (!isAuthSessionCurrent(owner)) return;
      const brief: Message = {
        id: `prep-brief-${Date.now()}`,
        role: 'assistant',
        content: response.content,
        chips: response.chips,
        receiptId: response.receiptId,
        timestamp: new Date(),
      };
      setMessages([brief]);
      setPrepState('ready');
      if (threadIdRef.current) {
        if (!isAuthSessionCurrent(owner)) return;
        const result = await chatThreadService.appendMessages(
          threadIdRef.current,
          [{ role: 'assistant', content: response.content, receipt_id: response.receiptId }],
          { signal: owner.signal },
        );
        if (!isAuthSessionCurrent(owner)) return;
        threadSummaryRef.current = result.summary ?? null;
      }
    } catch {
      if (isAuthSessionCurrent(owner)) setPrepState('error');
    }
  };

  // Session failed to load
  if (sessionLoadFailed) {
    return (
      <div className="size-full flex flex-col items-center justify-center gap-4 px-8" style={{ background: 'var(--app-bg)' }}>
        <p className="text-sm text-center" style={{ color: 'var(--app-text-muted)' }}>Session not found</p>
        <button
          onClick={() => navigate(-1)}
          className="px-4 py-2 rounded-lg text-sm border"
          style={{ background: 'var(--app-surface)', borderColor: 'var(--app-border)', color: 'var(--app-text)' }}
        >
          Go back
        </button>
      </div>
    );
  }

  // Session still loading from DB
  if (!session) {
    return (
      <div className="size-full flex flex-col items-center justify-center gap-3" style={{ background: 'var(--app-bg)' }}>
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--app-accent)' }} />
        <p className="text-sm" style={{ color: 'var(--app-text-muted)' }}>Loading session…</p>
      </div>
    );
  }

  if (!prepEnabled) {
    return (
      <div className="size-full flex flex-col items-center justify-center gap-4 px-8" style={{ background: 'var(--app-bg)' }}>
        <p className="text-sm text-center" style={{ color: 'var(--app-text-muted)' }}>Prep with AI is unavailable for this session type.</p>
        <button onClick={() => navigate(`/session/${session.id}`)} className="px-4 py-2 rounded-lg text-sm border" style={{ background: 'var(--app-surface)', borderColor: 'var(--app-border)', color: 'var(--app-text)' }}>View session details</button>
      </div>
    );
  }

  const sessionTime = formatTimeRange(session.start_time, session.end_time);
  const sessionDuration = formatDuration(session.start_time, session.end_time);
  const sessionRoom = session.room ?? session.location ?? 'TBD';
  const sessionTags = session.tags ?? [];

  return (
    <div className="size-full flex flex-col overflow-hidden" style={{ background: 'var(--app-bg)' }}>

      {/* ── Header ── */}
      <div
        className="app-screen-header flex items-center justify-between px-4 pt-12 pb-3 shrink-0 border-b"
        style={{ background: 'var(--app-bg)', borderColor: 'var(--app-border)' }}
      >
        <button
          onClick={() => navigate(-1)}
          className="size-10 rounded-full flex items-center justify-center active:scale-90 transition-transform"
          style={{ background: 'var(--app-surface)' }}
        >
          <ArrowLeft className="w-5 h-5" style={{ color: 'var(--app-text)' }} />
        </button>

        <div className="flex items-center gap-2">
          <div
            className="size-7 rounded-full flex items-center justify-center"
            style={{ background: 'var(--app-tag-bg)' }}
          >
            <Zap className="w-3.5 h-3.5" style={{ color: 'var(--app-accent)' }} />
          </div>
          <span className="text-sm" style={{ color: 'var(--app-text)' }}>Session Prep</span>
        </div>

        <div className="flex gap-1">
          <button
            onClick={handleRegenerate}
            className="size-10 rounded-full flex items-center justify-center active:scale-90 transition-transform"
            style={{ background: 'var(--app-surface)' }}
            title="Regenerate brief"
          >
            <RefreshCw className="w-4 h-4" style={{ color: 'var(--app-text-subtle)' }} />
          </button>
          {!preview && isEnabled('notes') && (
            <button
              onClick={saveLatestAsNote}
              className="size-10 rounded-full flex items-center justify-center active:scale-90 transition-transform"
              style={{ background: 'var(--app-surface)' }}
            >
              <Bookmark
                className="w-4 h-4"
                style={isSaved
                  ? { fill: 'var(--app-accent)', color: 'var(--app-accent)' }
                  : { color: 'var(--app-text-subtle)' }}
              />
            </button>
          )}
        </div>
      </div>

      {/* ── Session context banner ── */}
      <div
        className="mx-4 mt-3 rounded-xl px-4 py-3 border shrink-0"
        style={{ background: 'var(--app-surface)', borderColor: 'var(--app-border)' }}
      >
        <p className="text-xs mb-0.5" style={{ color: 'var(--app-accent)' }}>
          Preparing for
        </p>
        <p className="text-sm leading-snug" style={{ color: 'var(--app-text)' }}>
          {session.title}
        </p>
        <div className="flex items-center gap-3 mt-1.5">
          <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--app-text-muted)' }}>
            <Clock className="w-3 h-3" />
            {sessionTime}{sessionDuration ? ` · ${sessionDuration}` : ''}
          </span>
          <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--app-text-muted)' }}>
            <MapPin className="w-3 h-3" />
            {sessionRoom}
          </span>
        </div>
        <div className="flex gap-2 mt-2 flex-wrap">
          {sessionTags.map(tag => (
            <span
              key={tag}
              className="text-xs px-2 py-0.5 rounded-full"
              style={{ background: 'var(--app-tag-bg)', color: 'var(--app-accent)' }}
            >
              {tag}
            </span>
          ))}
        </div>

        {/* Quick nav: back to session detail */}
        <button
          onClick={() => navigate(`/session/${session.id}`)}
          className="mt-2.5 flex items-center gap-1 text-xs active:opacity-70"
          style={{ color: 'var(--app-text-muted)' }}
        >
          View session details
          <ChevronRight className="w-3 h-3" />
        </button>
      </div>

      {/* ── Messages ── */}
      <div className="flex-1 overflow-y-auto px-4 py-4">

        {/* Loading state */}
        {prepState === 'loading' && (
          <div className="flex flex-col items-center justify-center gap-3 py-12">
            <div
              className="size-12 rounded-full flex items-center justify-center"
              style={{ background: 'var(--app-tag-bg)' }}
            >
              <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--app-accent)' }} />
            </div>
            <p className="text-sm" style={{ color: 'var(--app-text-muted)' }}>
              Generating your prep brief…
            </p>
            <p className="text-xs text-center" style={{ color: 'var(--app-text-muted)', opacity: 0.7 }}>
              Analyzing session content
              {isEnabled('papers') ? ', related papers' : ''}
              {isEnabled('speakers') ? ', and speaker background' : ''}
            </p>
          </div>
        )}

        {/* Error state */}
        {prepState === 'error' && (
          <div className="flex flex-col items-center gap-3 py-12">
            <p className="text-sm" style={{ color: 'var(--app-text-muted)' }}>
              Failed to generate prep brief
            </p>
            <button
              onClick={handleRegenerate}
              className="px-4 py-2 rounded-lg text-sm border"
              style={{
                background: 'var(--app-surface)',
                borderColor: 'var(--app-border)',
                color: 'var(--app-text)',
              }}
            >
              Try again
            </button>
          </div>
        )}

        {/* Messages */}
        {prepState === 'ready' && (
          <div className="flex flex-col gap-4">
            {messages.map(msg => (
              <div
                key={msg.id}
                className={`flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'}`}
              >
                {/* Bubble row */}
                <div className={`flex items-start gap-2 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                  {msg.role === 'assistant' && (
                    <div
                      className="size-8 rounded-full flex items-center justify-center mt-0.5 shrink-0 border"
                      style={{ background: 'var(--app-tag-bg)', borderColor: 'var(--app-tag-border)' }}
                    >
                      <Zap className="w-4 h-4" style={{ color: 'var(--app-accent)' }} />
                    </div>
                  )}
                  <div
                    className={`max-w-[88%] rounded-2xl px-4 py-3 text-sm ${
                      msg.role === 'user' ? 'rounded-tr-sm' : 'rounded-tl-sm border'
                    }`}
                    style={
                      msg.role === 'user'
                        ? { background: 'var(--app-accent-solid)', color: '#fff' }
                        : {
                            background: 'var(--app-surface)',
                            borderColor: 'var(--app-border)',
                            color: 'var(--app-text)',
                          }
                    }
                  >
                    {msg.role === 'assistant' ? (
                      <MarkdownContent content={msg.content} />
                    ) : (
                      <p className="leading-relaxed text-sm">{msg.content}</p>
                    )}
                  </div>
                </div>

                {/* Action chips */}
                <ReportAIResponseButton receiptId={msg.receiptId} />
                {msg.role === 'assistant' && filterActionChips(msg.chips, isEnabled).length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2 ml-10">
                    {filterActionChips(msg.chips, isEnabled).map(chip => (
                      <button
                        key={chip.action}
                        onClick={() => handleChipAction(chip)}
                        className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border active:scale-95 transition-transform"
                        style={{
                          background: 'var(--app-surface)',
                          borderColor: 'var(--app-accent-border)',
                          color: 'var(--app-accent)',
                        }}
                      >
                        <ChipIcon name={chip.icon} />
                        {chip.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {/* Typing indicator */}
            {isLoading && (
              <div className="flex items-start gap-2">
                <div
                  className="size-8 rounded-full flex items-center justify-center shrink-0 border"
                  style={{ background: 'var(--app-tag-bg)', borderColor: 'var(--app-tag-border)' }}
                >
                  <Zap className="w-4 h-4" style={{ color: 'var(--app-accent)' }} />
                </div>
                <div
                  className="rounded-2xl rounded-tl-sm px-4 py-3 border"
                  style={{ background: 'var(--app-surface)', borderColor: 'var(--app-border)' }}
                >
                  <div className="flex flex-col gap-1.5">
                    <div className="flex gap-1.5 items-center h-4">
                      {[0, 150, 300].map(delay => (
                        <div
                          key={delay}
                          className="w-1.5 h-1.5 rounded-full animate-bounce"
                          style={{ background: 'var(--app-accent)', animationDelay: `${delay}ms` }}
                        />
                      ))}
                    </div>
                    {progressLabel && (
                      <p className="text-xs" style={{ color: 'var(--app-text-muted)' }}>
                        {progressLabel}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* ── Quick-prompt shortcuts (above input) ── */}
      {prepState === 'ready' && messages.length <= 1 && !isLoading && (
        <div
          className="px-4 pb-2 shrink-0 border-t pt-2"
          style={{ borderColor: 'var(--app-border)' }}
        >
          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
            {[
              ...(isEnabled('speakers') ? ['Generate Q&A questions'] : []),
              ...(isEnabled('papers') ? ['Background reading'] : []),
              'Key topics to watch',
              ...(isEnabled('agenda') ? ['Related sessions'] : []),
            ].map(s => (
              <button
                key={s}
                onClick={() => sendMessage(s)}
                className="text-xs px-3 py-1.5 rounded-full border whitespace-nowrap active:scale-95 transition-transform shrink-0"
                style={{
                  background: 'var(--app-surface)',
                  borderColor: 'var(--app-accent-border)',
                  color: 'var(--app-text-subtle)',
                }}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── Input bar ── */}
      <div
        className={`px-4 py-3 shrink-0 ${prepState === 'ready' && messages.length > 1 ? 'border-t' : ''}`}
        style={{ borderColor: 'var(--app-border)' }}
      >
        <div
          className="flex items-center gap-3 rounded-xl px-4 border transition-colors focus-within:border-[var(--app-accent)]"
          style={{ background: 'var(--app-surface)', borderColor: 'var(--app-input-border)' }}
        >
          <Zap className="w-4 h-4 shrink-0" style={{ color: 'var(--app-text-subtle)' }} />
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage()}
            placeholder="Ask about this session…"
            className="flex-1 bg-transparent py-3.5 text-sm outline-none"
            style={{ color: 'var(--app-text)' }}
          />
          <button
            onClick={() => sendMessage()}
            aria-label="Send message"
            disabled={!input.trim() || isLoading}
            className="size-8 rounded-lg flex items-center justify-center disabled:opacity-40 active:scale-90 transition-transform shrink-0"
            style={{ background: 'var(--app-accent-solid)' }}
          >
            <Send className="w-4 h-4 text-white" />
          </button>
        </div>

        {/* Bottom actions */}
        <div className="flex gap-2 mt-2.5">
          {!preview && isEnabled('notes') && (
            <button
              onClick={() => navigate(`/notes/${session.id}`)}
              className="flex-1 h-9 rounded-lg border text-xs flex items-center justify-center gap-1.5 active:scale-[0.98] transition-transform"
              style={{ background: 'var(--app-surface)', borderColor: 'var(--app-border)', color: 'var(--app-text-subtle)' }}
            >
              <FileText className="w-3.5 h-3.5" />
              Take Notes
            </button>
          )}
          <button
            onClick={() => navigate(`/session/${session.id}`)}
            className="flex-1 h-9 rounded-lg border text-xs flex items-center justify-center gap-1.5 active:scale-[0.98] transition-transform"
            style={{ background: 'var(--app-surface)', borderColor: 'var(--app-border)', color: 'var(--app-text-subtle)' }}
          >
            <ChevronRight className="w-3.5 h-3.5" />
            Session Details
          </button>
        </div>
      </div>
    </div>
  );
}
